import React, { Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
  faAndroid as fabFaAndroid,
} from '@fortawesome/free-brands-svg-icons';

import DrmSwitcher from 'Components/Product/ProductCommerce/DrmSwitcher';
import DrmBanner from 'Components/Product/DrmBanner/DrmBanner';
import GiveawayRequired from 'Components/Product/ProductCommerce/GiveawayRequired';
import GiveawaySoldOut from 'Components/Product/ProductCommerce/GiveawaySoldOut';
import MultiDrm from 'Components/Utility/MultiDrm';
import PackEditionSelector from 'Components/Bundle/Templates/PackTemplates/PackEditionSelector';
import PaymentCards from 'Components/Product/Payment/PaymentCards';
import ProductCover from 'Components/Utility/ProductCover';
import ProductEditionSelector from 'Components/Product/ProductCommerce/ProductEditionSelector';
import ProductHeaderButtons from 'Components/Product/ProductHeader/ProductHeaderButtons';
import ProductHeaderPrices from 'Components/Product/ProductHeader/ProductHeaderPrices';
import ProductPageTimer from 'Components/Product/Timer/ProductPageTimer';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import RegionLocked from 'Components/Product/ProductCommerce/RegionLocked';
import StarDealCountdown from 'Components/StarDeal/StarDealCountdown';
import WishlistButton from 'Containers/Wishlist/WishlistButton';
import useViewport from 'Hooks/useViewport';
import ProductMediaCarousel from 'Components/Product/Carousel/ProductMediaCarousel';
import ProductPrintCarousel from 'Components/Product/Carousel/ProductPrintCarousel';
import DangerousHTML from 'Components/Utility/DangerousHTML';
import MobileEditionSelector from 'Components/Product/ProductCommerce/MobileEditionSelector';

import 'Components/Product/ProductHeader/ProductHeader.scss';

const PayWhatYouWantButton = lazy(() => import(/* webpackChunkName: "PayWhatYouWant" */ 'Components/PayWhatYouWant/PayWhatYouWantButton'));

function ProductHeader({
  product,
  subscribed,
  screenshots,
  videos,
  bundleImages,
  bundleVideos,
}) {
  const cover = get(product, 'cover', '');
  const coverType = get(product, 'cover_type', '');
  const coverAltText = get(product, 'cover_alt_text', '');
  const currentDiscount = get(product, 'current_discount', {});
  const discountHideTimer = get(currentDiscount, 'hide_timer', false);
  const currentDiscountPercent = get(currentDiscount, 'percent', 0);
  const flashSale = get(currentDiscount, 'flash_sale', false);
  const displayType = get(product, 'display_type', '');
  const drm = get(product, 'drm', {});
  const drmEditions = get(product, 'drmEditions', []);
  const editions = get(product, 'editions', []);
  const excludedRegions = get(product, 'regions_excluded', []);
  const freeToPlay = get(product, 'free_to_play', false);
  const giveaway = get(product, 'giveaway', false);
  const giveawayNewsletter = get(product, 'giveaway_newsletter', false);
  const includedRegions = get(product, 'regions_included', []);
  const name = get(product, 'name', '');
  const packs = get(product, 'packs', []);
  const payWhatYouWant = get(product, 'pay_what_you_want', false);
  const presale = get(product, 'presale', false);
  const productId = get(product, '_id', '');
  const publishers = get(product, 'publishers', []);
  const slug = get(product, 'slug', '');
  const soldOut = get(product, 'sold_out', false);
  const regionLocked = get(product, 'region_locked', false);
  const type = get(product, 'type', '');
  const ztormId = get(product, 'ztormId', null);
  const tags = get(product, 'categories', []);
  const shortDescripion = get(product, 'seo.desc', null);
  const ageRatings = get(product, 'age', {});
  const unlinkCoverFromVideo = get(product, 'unlink_cover_from_video', false);
  const carouselType = get(product, 'carousel_type', '');
  const xboxPlatforms = get(product, 'xbox_platforms', {});
  const collections = get(product, 'collections', []);

  const today = new Date();

  const customSoldOutMessageText = get(product, 'custom_sold_out_message', '');
  const customSoldOutMessageIncluded = get(product, 'custom_sold_out_message_included', false);
  const customSoldOutMessageExpiry = get(product, 'custom_sold_out_message_expiry', null);
  const canShowCustomSoldOutMessage =
    customSoldOutMessageIncluded &&
    customSoldOutMessageText &&
    (!customSoldOutMessageExpiry || today <= new Date(customSoldOutMessageExpiry));

  const currentDrm = ProductRequirements.CurrentDrm(product);
  const validPlatforms = ProductRequirements.validPlatforms(product);
  const showDiscountDate = ProductRequirements.DiscountTime(product);
  const isStarDeal = ProductRequirements.isStarDeal(product);
  const keysAvailablePercentage = ProductRequirements.starDealKeyAvailablePercentage(product);
  const voucherRedeemProduct = (currentDrm === 'voucher' || currentDrm === 'redeem');
  const dates = ProductRequirements.ReleaseDates(product);
  const releaseDate = dates.releaseDate;
  const prerelease = dates.prerelease;
  const languagesCheck = ProductRequirements.LanguagesCheck(product);
  const noGermanRelease = languagesCheck.noGermanRelease;
  const steamReviewSummary = get(product, 'steamReviewSummary', null);


  let showTimer = false;
  if (
    showDiscountDate &&
    currentDiscount &&
    !soldOut &&
    !discountHideTimer
  ) {
    showTimer = true;
  }

  let showBigWishListButton = (presale || noGermanRelease) && !freeToPlay && !giveaway;

  if (
    !presale &&
    releaseDate &&
    (new Date(releaseDate) > today) &&
    product.availability &&
    product.availability.valid_from &&
    (new Date(product.availability.valid_from) > today)
  ) {
    showBigWishListButton = true;
  }

  const { isAboveSm, isAboveMd, isBelowMd, isBelowLg } = useViewport();

  let labelType = '';
  if (prerelease && !presale && !soldOut && !showBigWishListButton) labelType = 'preorder';
  if (isStarDeal) labelType = 'stardeal';
  if (flashSale) labelType = 'flashdeal';

  let showXboxPlatforms = false;
  if (
    !isEmpty(xboxPlatforms) &&
    (xboxPlatforms.xbox_series_x_s || xboxPlatforms.xbox_one || xboxPlatforms.windows_10_11)) {
    showXboxPlatforms = true;
  }

  const fantasyverseOfferSlugs = [
    'xenotype-pursuit',
    'urban-legend-hunters-the-baby-ghost-chapter-1-4',
    'cleopatra',
    'great-bodhisattva-peak-chapter-1-2',
    'himiko',
    'jack-and-the-wicked',
    'mad-bull-34',
    'ninja-1-flying-kato',
    'squirming-in-the-darkness',
    'freya-war-chapter-1-2',
    'jeanne-darc-chapter-1-2',
    'ahriman-revamped-volume-1-2',
    'papa-and-daddys-home-cooking-volume-1-4',
    'the-yokai-caretaker-vol-1',
    'the-yokai-caretaker-vol-2-5',
    'the-yokai-caretaker-vol-6-9',
    'together-with-zun-chan-volume-1-to-4',
    'the-hunt-chapter-1-2',
    'rogue-samurai-volume-1-3',
  ];

  let showFantasyverseOfferText = false;
  if (includes(collections, 'FantasyVerse') && (currentDiscountPercent * 100) === 50 && (includes(publishers, 'Dark Horse Comics') || includes(fantasyverseOfferSlugs, slug))) {
    showFantasyverseOfferText = true;
  }

  return (
    <section className={classNames('ProductHeader container')}>
      {isAboveMd &&
        <div className="ProductHeader__carousel">
          {carouselType !== 'non game' &&
            <ProductMediaCarousel
              cover={cover}
              coverAltText={coverAltText}
              sizesOverride="(min-width: 1520px) 870px, (min-width: 1200px) 740px, (min-width: 992px) 620px, (min-width: 768px) 704px, (min-width: 576px) 524px, 98vw"
              displayType={displayType}
              coverType={coverType}
              screenshots={screenshots}
              ageRatings={ageRatings}
              videos={videos}
              bundleImages={bundleImages}
              bundleVideos={bundleVideos}
              unlinkCoverFromVideo={unlinkCoverFromVideo}
              name={name}
            />
          }
          {carouselType === 'non game' &&
            <ProductPrintCarousel
              cover={cover}
              coverAltText={coverAltText}
              sizesOverride="(min-width: 1520px) 870px, (min-width: 1200px) 740px, (min-width: 992px) 620px, (min-width: 768px) 704px, (min-width: 576px) 524px, 98vw"
              displayType={displayType}
              coverType={coverType}
              screenshots={screenshots}
              ageRatings={ageRatings}
            />
          }
        </div>
      }

      {isBelowLg &&
        <div className={classNames('ProductHeader__cover', { 'ProductHeader__cover--noDrm': !currentDrm })}>
          <DrmBanner drm={currentDrm} steamReviewSummary={steamReviewSummary} />
          <ProductCover
            cover={cover}
            alt={coverAltText || `Product cover for ${name}`}
            sizesOverride="(min-width: 1520px) 870px, (min-width: 1200px) 740px, (min-width: 992px) 620px, (min-width: 768px) 704px, 98vw"
            displayType={displayType}
            dpr={slug === 'inside' ? 2 : 1}
            coverType={coverType}
          />
          {(labelType === 'preorder' || labelType === 'stardeal' || labelType === 'flashdeal') &&
            <div className={classNames(
              'ProductHeader__label',
              { 'ProductHeader__label--preorder': labelType === 'preorder' },
              { 'ProductHeader__label--stardeal': labelType === 'stardeal' },
              { 'ProductHeader__label--flashdeal': labelType === 'flashdeal' },
            )}
            >
              <div className="ProductHeader__label__content">
                <div className="ProductHeader__label__content__text">
                  {labelType === 'preorder' &&
                    <FormattedMessage id="product-header.label.pre-order" defaultMessage="Pre-order" />
                  }
                  {labelType === 'flashdeal' &&
                    <FormattedMessage id="product-header.label.flash-deal" defaultMessage="Flash Deal" />
                  }
                  {labelType === 'stardeal' &&
                    <img src="https://cdn.fanatical.com/production/brand/star-deal-logo-black.svg" alt="Star Deal" />
                  }
                </div>
                <div className="ProductHeader__label__content__slash" />
              </div>
              <div className="ProductHeader__label__triangle" />
            </div>
          }
        </div>
      }

      <div className="ProductHeader__details">
        {isAboveMd && (labelType === 'preorder' || labelType === 'stardeal' || labelType === 'flashdeal') &&
          <div className={classNames(
            'ProductHeader__label',
            { 'ProductHeader__label--preorder': labelType === 'preorder' },
            { 'ProductHeader__label--stardeal': labelType === 'stardeal' },
            { 'ProductHeader__label--flashdeal': labelType === 'flashdeal' },
          )}
          >
            <div className="ProductHeader__label__content">
              <div className="ProductHeader__label__content__text">
                {labelType === 'preorder' &&
                  <FormattedMessage id="product-header.label.pre-order" defaultMessage="Pre-order" />
                }
                {labelType === 'flashdeal' &&
                  <FormattedMessage id="product-header.label.flash-deal" defaultMessage="Flash Deal" />
                }
                {labelType === 'stardeal' &&
                  <img src="https://cdn.fanatical.com/production/brand/star-deal-logo-black.svg" alt="Star Deal" />
                }
              </div>
              <div className="ProductHeader__label__content__slash" />
            </div>
            <div className="ProductHeader__label__triangle" />
          </div>
        }

        <div className="ProductHeader__details__iconsWishlist">
          <div className="ProductHeader__details__icons">
            {isEmpty(drmEditions) && currentDrm &&
              <MultiDrm drm={drm} />
            }

            {drmEditions.length > 1 &&
              <DrmSwitcher drmEditions={drmEditions} currentDrm={currentDrm} />
            }

            <div className="ProductHeader__details__icons__platforms">
              {includes(validPlatforms, 'windows') &&
                <span>
                  <FontAwesomeIcon icon={fabFaWindows} />
                </span>
              }
              {includes(validPlatforms, 'mac') &&
                <span>
                  <FontAwesomeIcon icon={fabFaApple} />
                </span>
              }
              {includes(validPlatforms, 'linux') &&
                <span>
                  <FontAwesomeIcon icon={fabFaLinux} />
                </span>
              }
              {currentDrm === 'utalk' &&
                <span>
                  <FontAwesomeIcon icon={fabFaAndroid} />
                </span>
              }
            </div>
          </div>

          {!showBigWishListButton && isAboveMd &&
            <WishlistButton
              page="product"
              slug={slug}
              product={product}
              productId={productId}
            />
          }
        </div>

        {showXboxPlatforms &&
          <div className="xboxPlatforms">
            <FormattedMessage id="product-header.details.xbox-platforms.title" defaultMessage="Playable on:" />
            {xboxPlatforms.xbox_series_x_s &&
              <span className="xboxPlatforms__platform">Xbox Series X|S</span>
            }
            {xboxPlatforms.xbox_one &&
              <span className="xboxPlatforms__platform">Xbox One</span>
            }
            {xboxPlatforms.windows_10_11 &&
              <span className="xboxPlatforms__platform">Windows 10</span>
            }
          </div>
        }

        {isAboveMd && !isEmpty(tags) &&
          <div className="ProductHeader__details__tags">
            {tags.slice(0, 4).map((tag) => {
              if (tag.link) {
                return (
                  <Link to={`/categories/${tag.slug}`} className="ProductHeader__details__tags__tag" key={tag.name}>
                    {tag.name}
                  </Link>
                );
              }
              return (
                <div className="ProductHeader__details__tags__tag" key={tag.name}>
                  {tag.name}
                </div>
              );
            })}
          </div>
        }

        {isAboveMd && shortDescripion &&
          <DangerousHTML
            html={shortDescripion}
            className="ProductHeader__details__shortDescription"
          />
        }

        {!isEmpty(editions) &&
          <>
            {isBelowLg && <MobileEditionSelector editions={editions} productSlug={slug} />}
            {isAboveMd && <ProductEditionSelector product={product} />}
          </>
        }

        {!isEmpty(packs) &&
          <PackEditionSelector packs={packs} slug={slug} />
        }

        {!regionLocked &&
          <ProductHeaderPrices product={product} />
        }

        {giveaway && giveawayNewsletter && !subscribed && !soldOut &&
          <GiveawayRequired />
        }

        {giveaway && soldOut &&
          <GiveawaySoldOut subscribed={subscribed} />
        }

        {!regionLocked &&
          <ProductHeaderButtons
            product={product}
            showBigWishListButton={showBigWishListButton}
          />
        }

        {payWhatYouWant && !presale && !soldOut && !regionLocked &&
          <div className="PayWhatYouWant">
            <Suspense fallback={<div />}>
              <PayWhatYouWantButton
                showPriceMessage
                product={product}
                currentDiscount={currentDiscount}
              />
            </Suspense>
          </div>
        }

        {!showTimer && !isStarDeal && !regionLocked && showFantasyverseOfferText &&
          <Link className="ProductHeader__details__offerText" to="/fantasyverse">
            <FormattedMessage
              id="product-header.details.50-percent-offer.fantasyverse"
              defaultMessage="50% off Selected Content for a Limited Time"
            />
          </Link>
        }

        {showTimer && !isStarDeal && !regionLocked &&
          <ProductPageTimer
            productHeader
            startDate={Date.parse(currentDiscount.from)}
            endDate={Date.parse(currentDiscount.until)}
          />
        }

        {isStarDeal && !soldOut && !regionLocked &&
          <StarDealCountdown
            keysAvailablePercentage={keysAvailablePercentage}
            soldOut={soldOut}
            currentDiscount={currentDiscount}
          />
        }

        {soldOut && !giveaway && !freeToPlay && !presale &&
          <div className="ProductHeader__details__soldOut">
            {canShowCustomSoldOutMessage ?
              <DangerousHTML
                html={customSoldOutMessageText}
                className="ProductHeader__details__soldOut__custom"
              />
              :
              <React.Fragment>
                {type === 'bundle' ?
                  <React.Fragment>
                    <div className="ProductHeader__details__soldOut__title">
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                      <FormattedMessage id="product-header.details.bundle-eneded.title" defaultMessage="This bundle has ended" />
                    </div>
                    <div className="ProductHeader__details__soldOut__text">
                      <FormattedMessage
                        id="product-header.details.bundle-ended.message"
                        defaultMessage="You can view our latest bundles {link}."
                        values={{
                          link: <Link to="/bundle"><FormattedMessage id="product-header.details.bundle-eneded.message.link" defaultMessage="here" /></Link>,
                        }}
                      />
                    </div>
                  </React.Fragment>
                  :
                  <React.Fragment>
                    <div className="ProductHeader__details__soldOut__title">
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                      <FormattedMessage id="product-header.details.sold-out.title" defaultMessage="Sold Out" />
                    </div>
                    <div className="ProductHeader__details__soldOut__text">
                      <FormattedMessage id="product-header.details.sold-out.message" defaultMessage="Sorry, this product is temporarily unavailable." />
                    </div>
                  </React.Fragment>
                }
              </React.Fragment>
            }
          </div>
        }

        {!soldOut && !voucherRedeemProduct &&
          <RegionLocked included={includedRegions} excluded={excludedRegions} ztormId={ztormId} />
        }

        {!giveaway && isAboveSm &&
          <PaymentCards />
        }
      </div>

      {!giveaway && isBelowMd &&
        <div className="mt-4">
          <PaymentCards />
        </div>
      }
    </section>
  );
}

ProductHeader.propTypes = {
  product: PropTypes.shape({}),
  subscribed: PropTypes.bool,
  screenshots: PropTypes.arrayOf(PropTypes.object),
  videos: PropTypes.arrayOf(PropTypes.object),
  bundleImages: PropTypes.arrayOf(PropTypes.object),
  bundleVideos: PropTypes.arrayOf(PropTypes.object),
};

ProductHeader.defaultProps = {
  product: {},
  subscribed: false,
  screenshots: [],
  videos: [],
  bundleImages: [],
  bundleVideos: [],
};

const mapStateToProps = ({
  cart: { allSlugs },
  auth: { email_newsletter: emailNewsletter, localNewsletterConsent },
  voucher: { voucher },
}) => ({
  allSlugs,
  subscribed: emailNewsletter,
  localNewsletterConsent,
  voucher,
});

export default connect(mapStateToProps)(ProductHeader);
